/**
 * carousel-ui.tsx
 * ─────────────────────────────────────────────────────────
 * Shared primitives for ALL landing-page carousels.
 * Single source of truth for shadows, dots and slim-tab arrows.
 *
 * Usage:
 *   import { CarouselDots, CarouselArrow, CARD_SHADOW, CARD_SHADOW_POPULAR } from "./carousel-ui";
 */

import { ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";

/* ─────────────────────────────── Shadow tokens ────────────────────────────── */

/** Default card shadow — short, no section bleed */
export const CARD_SHADOW =
  "shadow-[0_8px_20px_rgba(110,79,52,0.07)]";

/** Hover shadow — slightly deeper, still contained */
export const CARD_SHADOW_HOVER =
  "hover:shadow-[0_12px_28px_rgba(110,79,52,0.11)]";

/** Popular / highlighted card shadow */
export const CARD_SHADOW_POPULAR =
  "shadow-[0_8px_24px_rgba(239,123,67,0.10)]";

/* ─────────────────────────────── CarouselArrow ────────────────────────────── */

type CarouselArrowProps = {
  direction: "left" | "right";
  onClick: () => void;
  disabled?: boolean;
  ariaLabel: string;
  /** Override top positioning when inner track has bottom padding.
   *  Pass e.g. "top-[calc(50%-1rem)]" when pb-8 inflates the relative container.
   *  Defaults to "top-1/2".
   */
  topClass?: string;
  /** Extra classes for responsive visibility (e.g. "flex sm:hidden") */
  className?: string;
};

export function CarouselArrow({
  direction,
  onClick,
  disabled = false,
  ariaLabel,
  topClass = "top-1/2",
  className,
}: CarouselArrowProps) {
  return (
    <button
      type="button"
      onClick={onClick}
      aria-label={ariaLabel}
      tabIndex={disabled ? -1 : 0}
      className={cn(
        "pointer-events-auto absolute -translate-y-1/2 z-20",
        "flex h-12 w-6 items-center justify-center",
        "bg-[#fff7ef]/80 text-[#9a8a7d] backdrop-blur-sm",
        "transition-colors duration-200 ease-out active:text-[#ef7b43] focus-visible:outline-none",
        direction === "left" ? "left-0 rounded-r-full" : "right-0 rounded-l-full",
        topClass,
        disabled ? "opacity-0 pointer-events-none" : "opacity-100",
        className,
      )}
    >
      {direction === "left" ? (
        <ChevronLeft className="h-3.5 w-3.5" />
      ) : (
        <ChevronRight className="h-3.5 w-3.5" />
      )}
    </button>
  );
}

/* ─────────────────────────────── CarouselDots ─────────────────────────────── */

type CarouselDotsProps = {
  count: number;
  activeIndex: number;
  onDotClick: (i: number) => void;
  ariaLabelFn?: (i: number) => string;
  /** Extra classes applied to the wrapper div. Defaults to "mt-5". */
  className?: string;
};

export function CarouselDots({
  count,
  activeIndex,
  onDotClick,
  ariaLabelFn,
  className,
}: CarouselDotsProps) {
  return (
    <div className={cn("flex items-center justify-center gap-2 mt-5", className)}>
      {Array.from({ length: count }, (_, i) => (
        <button
          key={i}
          type="button"
          onClick={() => onDotClick(i)}
          aria-label={ariaLabelFn ? ariaLabelFn(i) : `Item ${i + 1}`}
          className="relative flex items-center justify-center transition-colors duration-200 ease-out"
          style={{ width: "1.5rem", height: "1.5rem" }}
        >
          <span
            className="rounded-full block transition-colors duration-200 ease-out"
            style={{
              width: i === activeIndex ? "1.75rem" : "0.5rem",
              height: "0.5rem",
              background: i === activeIndex ? "#ef7b43" : "#d9cec5",
            }}
          />
        </button>
      ))}
    </div>
  );
}

/* ─────────────────────────────── CarouselHint ─────────────────────────────── */

type CarouselHintProps = {
  text: string;
  className?: string;
};

export function CarouselHint({ text, className }: CarouselHintProps) {
  return (
    <p className={cn("text-center text-xs text-[#9a8a7d] mt-3", className)}>
      {text}
    </p>
  );
}
